import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCompanyContext } from './use-company-context';

export interface FleetDashboardStats {
  activeVehicles: number;
  totalDrivers: number;
  pendingMaintenances: number;
  activeAlerts: number;
  monthlyFuelCost: number;
  totalTrips: number;
  averageEfficiency: number;
}

export function useFleetDashboardStats() {
  const { companyId } = useCompanyContext();
  
  console.log('🚚 [useFleetDashboardStats] companyId:', companyId);

  return useQuery({
    queryKey: ['fleet-dashboard-stats', companyId],
    queryFn: async (): Promise<FleetDashboardStats> => {
      if (!companyId) {
        throw new Error('Company ID não encontrado');
      }

      // Get active vehicles count
      const { data: vehicles, error: vehiclesError } = await supabase
        .from('fleet_vehicles')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'active');

      if (vehiclesError) throw vehiclesError;

      // Get drivers count
      const { data: drivers, error: driversError } = await supabase
        .from('fleet_drivers')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'active');

      if (driversError) throw driversError;

      // Get vehicles in maintenance status
      const { data: maintenances, error: maintenancesError } = await supabase
        .from('fleet_vehicles')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'maintenance');

      if (maintenancesError) throw maintenancesError;

      // Get active alerts
      const { data: alerts, error: alertsError } = await supabase
        .from('fleet_alerts')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'active');

      if (alertsError) throw alertsError;

      // Get monthly fuel cost (current month)
      const currentMonth = new Date();
      const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const firstDayOfNextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
      
      const { data: fuelLogs, error: fuelError } = await supabase
        .from('fleet_fuel_logs')
        .select('liters, price_unit')
        .eq('company_id', companyId)
        .gte('occurred_at', firstDayOfMonth.toISOString())
        .lt('occurred_at', firstDayOfNextMonth.toISOString());

      if (fuelError) throw fuelError;

      const monthlyFuelCost = fuelLogs?.reduce((total, log) => {
        return total + (log.liters * (log.price_unit || 0));
      }, 0) || 0;

      // Get trips count (current month)
      const { data: trips, error: tripsError } = await supabase
        .from('fleet_trips')
        .select('id')
        .eq('company_id', companyId)
        .gte('created_at', firstDayOfMonth.toISOString())
        .lt('created_at', firstDayOfNextMonth.toISOString());

      if (tripsError) throw tripsError;

      return {
        activeVehicles: vehicles?.length || 0,
        totalDrivers: drivers?.length || 0,
        pendingMaintenances: maintenances?.length || 0,
        activeAlerts: alerts?.length || 0,
        monthlyFuelCost,
        totalTrips: trips?.length || 0,
        averageEfficiency: 0 // Calculate based on business logic
      };
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}